import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { SubscriptionLike } from '../../../models/common';
import { WindowReferenceService } from '../../../services/window-reference/window-reference.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';

/**
 * Sticky wrapper causes a element to become sticky by creating a canary element that
 * swaps position with the element so that the element can tell if it needs to be fixed or relative.
 */
@LifecycleAware()
@Component({
    selector: 'lsq-sticky-wrapper',
    template: `
        <div
            #stickyElementRef
            [ngClass]="{
                'stick-to-top': isSticky && !isTooLarge
            }"
            [ngStyle]="{
                position: isSticky && !isTooLarge ? 'fixed' : 'relative',
                width: isSticky && elementWidth ? elementWidth : ''
            }"
        >
            <ng-content></ng-content>
        </div>
        <div
            #stickyCanaryRef
            [ngClass]="{
                'stick-to-top': !isSticky || isTooLarge
            }"
            [ngStyle]="{
                position: isSticky && !isTooLarge ? 'relative' : 'fixed',
                height: 0,
                width: '100%'
            }"
        ></div>
    `
})
export class LsqStickyWrapperComponent implements OnInit, OnDestroy {
    @Input() public stickyEnabled = true;

    @ViewChild('stickyElementRef', { static: true }) public stickyElementRef: ElementRef;
    @ViewChild('stickyCanaryRef', { static: true }) public stickyCanaryRef: ElementRef;

    public isSticky = false;
    public isTooLarge = false;
    public elementWidth: string;

    private _scrollSubscription: SubscriptionLike;

    constructor(
        private windowReferenceService: WindowReferenceService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    public ngOnInit() {
        this._scrollSubscription = this.windowReferenceService
            .createScrollStream(true)
            .subscribe(() => {
                if (!this.stickyEnabled) {
                    this.isSticky = false;
                    return;
                }

                if (!this.isSticky) {
                    this.isSticky = !!(
                        this.stickyElementRef.nativeElement.getBoundingClientRect().top <
                        this.stickyCanaryRef.nativeElement.getBoundingClientRect().top
                    );
                    if (this.isSticky) {
                        // We set the width to the current width so that it renders at the same size.
                        this.elementWidth = `${
                            this.stickyElementRef.nativeElement.getBoundingClientRect().width
                        }px`;
                    }
                } else {
                    this.isSticky = !!(
                        this.stickyElementRef.nativeElement.getBoundingClientRect().top >
                        this.stickyCanaryRef.nativeElement.getBoundingClientRect().top
                    );
                }
                this.handleViewportLimitsChanges();
            });

        this.windowReferenceService.resizeStream.pipe(forLifeOf(this)).subscribe(() => {
            this.updateElementWidth();
            this.handleViewportLimitsChanges();
        });
    }

    public ngOnDestroy(): void {
        if (this._scrollSubscription) {
            this._scrollSubscription.unsubscribe();
        }
    }

    public updateElementWidth(): void {
        if (this.isSticky && !this.isTooLarge) {
            this.elementWidth = `${
                this.stickyCanaryRef.nativeElement.getBoundingClientRect().width
            }px`;
            this.changeDetectorRef.markForCheck();
        }
    }

    public handleViewportLimitsChanges(): void {
        const oldIsTooLarge = this.isTooLarge;
        if (this.isSticky && !this.isTooLarge) {
            this.isTooLarge =
                this.stickyElementRef.nativeElement.getBoundingClientRect().height >
                window.innerHeight - this.stickyElementRef.nativeElement.getBoundingClientRect().y;
        } else if (!!this.stickyElementRef) {
            this.isTooLarge =
                this.stickyElementRef.nativeElement.getBoundingClientRect().height >
                window.innerHeight - this.stickyCanaryRef.nativeElement.getBoundingClientRect().y;
        }
        if (oldIsTooLarge !== this.isTooLarge) {
            this.elementWidth = `${
                this.stickyElementRef.nativeElement.getBoundingClientRect().width
            }px`;
            Utilities.executeAfterRender(() => {
                this.updateElementWidth();
            });
        }
    }
}
